import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconShieldExclamation,
  IconMessageCircle,
  IconMessageDots,
  IconBug,
  IconSend,
  IconMail,
  IconCirclePlus,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconAddressBook,
  IconArrowRight,
  IconX,
} from "@tabler/icons-react";
import classes from "./support.module.css";
import { checkMail } from "../../../utils/functions";
import { errorHandler, toastSuccess } from "../../../utils";
import { postSupportComplaint } from "../../../services/talentCalls";
import PhoneNumberInput from "../../../components/UI/PhoneNumber/phonenumber";

const FAQS = [
  {
    q: "How do I apply for a job or gig?",
    a: "Browse open roles on the Job Feed or use Advanced Search to filter by title, skills, salary, and work mode. Open a listing and click Apply Now — you'll need a verified profile and a submitted Psychometric Test first.",
  },
  {
    q: "Why do I need to complete the Psychometric Test?",
    a: "The test helps employers understand how you approach real work scenarios. It's required to give you better chances when you apply to any job — you can start it any time from the Test page in the sidebar.",
  },
  {
    q: "How do I check if I've been matched to a job?",
    a: "Head to Matches in the sidebar. It's split into Active, Viewed, Applied, and Starred tabs so you can track where every match stands.",
  },
  {
    q: "Why is my profile showing as unverified?",
    a: "Verification requires completing onboarding — resume, personal info, education, skills, work history, and compensation. Finish any missing steps from Talent Onboarding and we will mark your profile as verified once we confirm.",
  },
  {
    q: "How do I unlock AI Insights improvement plans?",
    a: "Your core AI Insights are free. The deeper Improvement test and Learning Path recommendations unlock with a talent subscription, which you can start from the AI Insights page.",
  },
  {
    q: "How do I pause notifications or suspend my account?",
    a: "Go to Settings → Configurations. You can suspend your account until a chosen date and reactivate it any time before then, right from the same page.",
  },
  {
    q: "How do I download my account or job data?",
    a: "Go to Settings → Account Data. You can download a full account export or a summary of your completed jobs as a PDF.",
  },
  {
    q: "How do I reset my account password?",
    a: "Click Forgot Password on the login screen and enter your email — you'll get a reset link shortly.",
  },
];

const TalentSupport = () => {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null); // 'report' | 'contact' | 'bug' | null
  const [openFaq, setOpenFaq] = useState(null);
  const [faqSearch, setFaqSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [contactData, setContactData] = useState({ name: "", email: "", phone: "", message: "" });
  const [bugData, setBugData] = useState({ name: "", email: "", page: "", issue: "" });
  const [reportData, setReportData] = useState({ name: "", email: "", summary: "", issue: "" });

  const filteredFaqs = FAQS.filter(
    (f) =>
      !faqSearch.trim() ||
      f.q.toLowerCase().includes(faqSearch.toLowerCase()) ||
      f.a.toLowerCase().includes(faqSearch.toLowerCase()),
  );

  const closeModal = () => setModal(null);

  const submitContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await postSupportComplaint({
        supportType: "Contact",
        fullName: contactData.name,
        email: contactData.email,
        phoneNumber: contactData.phone,
        message: contactData.message,
      });
      toastSuccess("Thanks for your feedback — we'll get back to you soon.");
      setContactData({ name: "", email: "", phone: "", message: "" });
      closeModal();
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const submitBug = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await postSupportComplaint({
        supportType: "LogABug",
        fullName: bugData.name,
        email: bugData.email,
        pageOrFeature: bugData.page,
        message: bugData.issue,
      });
      toastSuccess("Thanks — your bug report has been logged.");
      setBugData({ name: "", email: "", page: "", issue: "" });
      closeModal();
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const submitReport = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await postSupportComplaint({
        supportType: "ReportBehavior",
        fullName: reportData.name,
        email: reportData.email,
        summaryOfReport: reportData.summary,
        message: reportData.issue,
      });
      toastSuccess("Thanks — your report has been submitted to our trust & safety team.");
      setReportData({ name: "", email: "", summary: "", issue: "" });
      closeModal();
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.main}>
      <div className={classes.pageHeader}>
        <div className={classes.breadcrumb}>
          <span className={classes.breadcrumbLink} onClick={() => navigate("/talent-dashboard")}>
            Dashboard
          </span>
          <IconChevronRight size={12} />
          <span>Support</span>
        </div>
        <h1 className={classes.pageTitle}>Support Centre</h1>
      </div>

      <div className={classes.hero}>
        <div className={classes.heroInner}>
          <div className={classes.heroText}>
            <div className={classes.heroEyebrow}>We're here to help</div>
            <h2 className={classes.heroTitle}>How can we help you today?</h2>
            <p className={classes.heroSub}>
              Browse common questions, report an issue, or get in touch with our team. We typically respond
              within a few hours.
            </p>
          </div>
        </div>
      </div>

      <div className={classes.grid}>
        <div className={classes.mainCol}>
          <div>
            <div className={classes.sectionHead}>
              <span className={classes.sectionHeadTitle}>Get Support</span>
            </div>
            <div className={classes.supportCards}>
              <div className={`${classes.supportCard} ${classes.cardReport}`} onClick={() => setModal("report")}>
                <div className={`${classes.scIconWrap} ${classes.scIconRed}`}>
                  <IconShieldExclamation size={24} />
                </div>
                <div className={classes.scTitle}>Report Behaviour</div>
                <p className={classes.scDesc}>
                  Concerned about a user's conduct? Flag inappropriate behaviour so our trust &amp; safety team
                  can review it.
                </p>
                <button className={`${classes.scCta} ${classes.scCtaRed}`}>
                  <IconSend size={14} /> Report Now
                </button>
              </div>

              <div className={`${classes.supportCard} ${classes.cardContact}`} onClick={() => setModal("contact")}>
                <div className={`${classes.scIconWrap} ${classes.scIconBlue}`}>
                  <IconMessageCircle size={24} />
                </div>
                <div className={classes.scTitle}>Contact Us</div>
                <p className={classes.scDesc}>
                  Have a question or general enquiry? Our support team is ready to help you with anything on
                  the platform.
                </p>
                <button className={`${classes.scCta} ${classes.scCtaBlue}`}>
                  <IconMail size={14} /> Get in Touch
                </button>
              </div>

              <div className={`${classes.supportCard} ${classes.cardBug}`} onClick={() => setModal("bug")}>
                <div className={`${classes.scIconWrap} ${classes.scIconAmber}`}>
                  <IconBug size={24} />
                </div>
                <div className={classes.scTitle}>Log a Bug</div>
                <p className={classes.scDesc}>
                  Found something broken or behaving unexpectedly? Help us improve by submitting a detailed bug
                  report.
                </p>
                <button className={`${classes.scCta} ${classes.scCtaAmber}`}>
                  <IconCirclePlus size={14} /> Log a Bug
                </button>
              </div>
            </div>
          </div>

          <div>
            <div className={classes.sectionHead}>
              <span className={classes.sectionHeadTitle}>Frequently Asked Questions</span>
              <span className={classes.sectionHeadBadge}>{FAQS.length} articles</span>
            </div>
            <div className={classes.faqList}>
              {filteredFaqs.length === 0 ? (
                <p className={classes.faqNoResults}>No articles match "{faqSearch}".</p>
              ) : (
                filteredFaqs.map((f, i) => {
                  const isOpen = openFaq === i;
                  return (
                    <div key={f.q} className={`${classes.faqItem} ${isOpen ? classes.faqItemOpen : ""}`}>
                      <button className={classes.faqQuestion} onClick={() => setOpenFaq(isOpen ? null : i)}>
                        <span className={classes.faqQLeft}>
                          <span className={classes.faqNum}>{i + 1}</span>
                          <span className={classes.faqQText}>{f.q}</span>
                        </span>
                        <IconChevronDown size={18} className={classes.faqChevron} />
                      </button>
                      {isOpen && <div className={classes.faqAnswer}>{f.a}</div>}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <aside className={classes.sidebar}>
          <div className={classes.sideCard}>
            <div className={classes.sideCardHeader}>
              <div className={classes.sideCardIcon}>
                <IconClock size={16} />
              </div>
              <span className={classes.sideCardTitle}>Response Times</span>
            </div>
            <div className={classes.sideCardBody}>
              <div className={classes.responseGrid}>
                <div className={classes.responseItem}>
                  <div className={classes.responseLabel}>Email</div>
                  <div className={classes.responseValue}>4–8</div>
                  <div className={classes.responseUnit}>hours avg.</div>
                </div>
                <div className={classes.responseItem}>
                  <div className={classes.responseLabel}>Live Chat</div>
                  <div className={classes.responseValue}>&lt; 5</div>
                  <div className={classes.responseUnit}>mins avg.</div>
                </div>
              </div>
              <p className={classes.sideNote}>
                Business hours: Mon–Fri, 9am–6pm WAT. Critical issues are handled 24/7.
              </p>
            </div>
          </div>

          <div className={classes.sideCard}>
            <div className={classes.sideCardHeader}>
              <div className={classes.sideCardIcon}>
                <IconAddressBook size={16} />
              </div>
              <span className={classes.sideCardTitle}>Contact Options</span>
            </div>
            <div className={classes.sideCardBody}>
              <div className={classes.contactList}>
                <a href="mailto:support@awasource.com" className={classes.contactOpt}>
                  <div className={`${classes.contactOptIcon} ${classes.contactOptIconBlue}`}>
                    <IconMail size={17} />
                  </div>
                  <div className={classes.contactOptInfo}>
                    <div className={classes.contactOptName}>Email Support</div>
                    <div className={classes.contactOptSub}>support@awasource.com</div>
                  </div>
                  <IconArrowRight size={15} className={classes.contactOptArrow} />
                </a>
                <div className={classes.contactOpt} onClick={() => setModal("contact")}>
                  <div className={`${classes.contactOptIcon} ${classes.contactOptIconGreen}`}>
                    <IconMessageDots size={17} />
                  </div>
                  <div className={classes.contactOptInfo}>
                    <div className={classes.contactOptName}>Live Chat</div>
                    <div className={classes.contactOptSub}>Pro &amp; Enterprise plans</div>
                  </div>
                  <IconArrowRight size={15} className={classes.contactOptArrow} />
                </div>
              </div>
            </div>
          </div>

        </aside>
      </div>

      {/* ── Contact Us modal ── */}
      {modal === "contact" && (
        <div className={classes.modalOverlay} onClick={closeModal}>
          <form className={classes.modalCard} onClick={(e) => e.stopPropagation()} onSubmit={submitContact}>
            <div className={classes.modalHead}>
              <div className={`${classes.modalHeadIcon} ${classes.modalHeadIconBlue}`}>
                <IconMessageCircle size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>Contact Us</div>
                <div className={classes.modalHeadSub}>We'll get back to you as soon as possible.</div>
              </div>
              <button type="button" className={classes.modalClose} onClick={closeModal} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Full Name*</label>
                <input
                  className={classes.formInput}
                  placeholder="Jane Doe"
                  value={contactData.name}
                  onChange={(e) => setContactData((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Email Address*</label>
                <input
                  className={classes.formInput}
                  placeholder="janedoe@awasource.com"
                  value={contactData.email}
                  onChange={(e) => setContactData((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className={`${classes.formGroup} ${classes.phoneGroup}`}>
                <PhoneNumberInput
                  label="Phone Number*"
                  value={contactData.phone}
                  onChange={(v) => setContactData((p) => ({ ...p, phone: v }))}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>How can we help you?*</label>
                <textarea
                  className={classes.formTextarea}
                  value={contactData.message}
                  onChange={(e) => setContactData((p) => ({ ...p, message: e.target.value }))}
                />
              </div>
            </div>
            <div className={classes.modalFoot}>
              <button type="button" className={`${classes.modalBtn} ${classes.modalBtnGhost}`} onClick={closeModal}>
                Cancel
              </button>
              <button
                type="submit"
                className={`${classes.modalBtn} ${classes.modalBtnPrimary}`}
                disabled={
                  !contactData.name || !checkMail(contactData.email) || !contactData.phone || !contactData.message || submitting
                }
              >
                <IconSend size={14} /> {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Log a Bug modal ── */}
      {modal === "bug" && (
        <div className={classes.modalOverlay} onClick={closeModal}>
          <form className={classes.modalCard} onClick={(e) => e.stopPropagation()} onSubmit={submitBug}>
            <div className={classes.modalHead}>
              <div className={`${classes.modalHeadIcon} ${classes.modalHeadIconAmber}`}>
                <IconBug size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>Log a Bug</div>
                <div className={classes.modalHeadSub}>Tell us about an issue you noticed.</div>
              </div>
              <button type="button" className={classes.modalClose} onClick={closeModal} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Full Name*</label>
                <input
                  className={classes.formInput}
                  placeholder="Jane Doe"
                  value={bugData.name}
                  onChange={(e) => setBugData((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Email Address*</label>
                <input
                  className={classes.formInput}
                  placeholder="janedoe@awasource.com"
                  value={bugData.email}
                  onChange={(e) => setBugData((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Page or feature where bug was found*</label>
                <input
                  className={classes.formInput}
                  placeholder="Job Feed, Advanced Search, etc."
                  value={bugData.page}
                  onChange={(e) => setBugData((p) => ({ ...p, page: e.target.value }))}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Tell us about the issue*</label>
                <textarea
                  className={classes.formTextarea}
                  value={bugData.issue}
                  onChange={(e) => setBugData((p) => ({ ...p, issue: e.target.value }))}
                />
              </div>
            </div>
            <div className={classes.modalFoot}>
              <button type="button" className={`${classes.modalBtn} ${classes.modalBtnGhost}`} onClick={closeModal}>
                Cancel
              </button>
              <button
                type="submit"
                className={`${classes.modalBtn} ${classes.modalBtnAmber}`}
                disabled={!bugData.name || !checkMail(bugData.email) || !bugData.page || !bugData.issue || submitting}
              >
                <IconCirclePlus size={14} /> {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Report Behaviour modal ── */}
      {modal === "report" && (
        <div className={classes.modalOverlay} onClick={closeModal}>
          <form className={classes.modalCard} onClick={(e) => e.stopPropagation()} onSubmit={submitReport}>
            <div className={classes.modalHead}>
              <div className={`${classes.modalHeadIcon} ${classes.modalHeadIconRed}`}>
                <IconShieldExclamation size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>Report Behaviour</div>
                <div className={classes.modalHeadSub}>Report inappropriate behaviour.</div>
              </div>
              <button type="button" className={classes.modalClose} onClick={closeModal} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Full Name*</label>
                <input
                  className={classes.formInput}
                  placeholder="Jane Doe"
                  value={reportData.name}
                  onChange={(e) => setReportData((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Email Address*</label>
                <input
                  className={classes.formInput}
                  placeholder="janedoe@awasource.com"
                  value={reportData.email}
                  onChange={(e) => setReportData((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Summary of Report*</label>
                <input
                  className={classes.formInput}
                  value={reportData.summary}
                  onChange={(e) => setReportData((p) => ({ ...p, summary: e.target.value }))}
                />
              </div>
              <div className={classes.formGroup}>
                <label className={classes.formLabel}>Details of Issue*</label>
                <textarea
                  className={classes.formTextarea}
                  value={reportData.issue}
                  onChange={(e) => setReportData((p) => ({ ...p, issue: e.target.value }))}
                />
              </div>
            </div>
            <div className={classes.modalFoot}>
              <button type="button" className={`${classes.modalBtn} ${classes.modalBtnGhost}`} onClick={closeModal}>
                Cancel
              </button>
              <button
                type="submit"
                className={`${classes.modalBtn} ${classes.modalBtnDanger}`}
                disabled={
                  !reportData.name || !checkMail(reportData.email) || !reportData.summary || !reportData.issue || submitting
                }
              >
                <IconSend size={14} /> {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TalentSupport;
